<template>
  <div>
    <app-template>
      <div class="min-h-screen">

        <!-- ── HEADER ─────────────────────────────────────────────────────────── -->
        <div class="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 class="text-2xl font-bold text-neutral-900 tracking-tight">Mon abonnement</h1>
          </div>
          <div class="flex items-center gap-2 text-xs text-neutral-500 bg-neutral-100 border border-neutral-200 rounded-full px-4 py-2">
            <i class="fas fa-lock text-green-500"></i>
            <span>Paiements sécurisés</span>
            <span class="text-neutral-300">·</span>
            <i class="fab fa-stripe text-xl" style="color:#635bff;"></i>
          </div>
        </div>

        <!-- ── LOADER ─────────────────────────────────────────────────────────── -->
        <div v-if="loading" class="flex flex-col items-center justify-center min-h-96 gap-3 text-neutral-400">
          <div class="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-orange-500 animate-spin"></div>
          <p class="text-sm">Chargement…</p>
        </div>

        <div v-else class="max-w-5xl mx-auto px-6 py-10">

          <!-- ══════════════════════════════════════════════════════════════════ -->
          <!--  ÉTAPE 1 — CHOISIR UNE FORMULE                                   -->
          <!-- ══════════════════════════════════════════════════════════════════ -->
          <section v-if="step === 'plans'">
            <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-6 flex items-center gap-2">
              <span class="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded">01</span>
              Choisissez votre formule
            </p>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              <button
                v-for="plan in plans"
                :key="plan.id"
                type="button"
                class="text-left bg-white rounded-xl border-2 p-7 cursor-pointer transition-all duration-150 relative focus:outline-none"
                :class="selectedPlan === plan.id
                  ? 'border-orange-500 shadow-[0_0_0_3px_rgba(240,90,0,0.12)]'
                  : 'border-neutral-200 hover:border-neutral-300 hover:shadow-md'"
                @click="selectedPlan = plan.id"
              >
                <div v-if="plan.recommended" class="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-neutral-900 text-white text-xs font-mono uppercase tracking-wider px-3 py-1 rounded-full whitespace-nowrap">
                  ⭐ Recommandé
                </div>
                <div class="flex items-center justify-between mb-5">
                  <div class="w-11 h-11 rounded-xl flex items-center justify-center text-lg" :class="plan.recommended ? 'bg-neutral-900 text-orange-500' : 'bg-orange-50 text-orange-500'">
                    <i :class="plan.icon"></i>
                  </div>
                  <div class="w-5 h-5 rounded-full border-2 flex items-center justify-center text-white transition-all" :class="selectedPlan === plan.id ? 'bg-orange-500 border-orange-500' : 'border-neutral-300'">
                    <i v-if="selectedPlan === plan.id" class="fas fa-check" style="font-size:9px;"></i>
                  </div>
                </div>
                <p class="font-bold text-lg text-neutral-900 mb-0.5">{{ plan.name }}</p>
                <p class="text-sm text-neutral-400 mb-5">{{ plan.tagline }}</p>
                <div class="flex items-baseline gap-0.5 mb-6">
                  <span class="text-5xl font-bold text-neutral-900 tracking-tighter">{{ plan.price_int }}</span>
                  <span class="text-2xl font-bold text-neutral-900">{{ plan.price_dec }}</span>
                  <span class="text-sm text-neutral-400 ml-1">€ HT / mois</span>
                </div>
                <ul class="space-y-2.5">
                  <li v-for="feature in plan.features" :key="feature.label" class="flex items-center gap-2.5 text-sm" :class="feature.included ? 'text-neutral-700' : 'text-neutral-300'">
                    <i class="text-xs w-4 shrink-0" :class="feature.included ? 'fas fa-check text-orange-500' : 'fas fa-minus text-neutral-300'"></i>
                    {{ feature.label }}
                  </li>
                </ul>
              </button>
            </div>

            <div class="flex flex-wrap gap-x-8 gap-y-2 mb-8 px-5 py-4 bg-white border border-neutral-200 rounded-xl text-sm text-neutral-500">
              <span class="flex items-center gap-2"><i class="fas fa-redo-alt text-orange-500 text-xs"></i> Sans engagement</span>
              <span class="flex items-center gap-2"><i class="fas fa-file-invoice text-orange-500 text-xs"></i> Facture PDF automatique</span>
              <span class="flex items-center gap-2"><i class="fas fa-shield-alt text-orange-500 text-xs"></i> 3D Secure inclus</span>
              <span class="flex items-center gap-2"><i class="fas fa-times-circle text-orange-500 text-xs"></i> Résiliable à tout moment</span>
            </div>

            <button
              class="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg shadow-[0_4px_14px_rgba(240,90,0,0.3)] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              :disabled="!selectedPlan"
              @click="goToPayment"
            >
              Continuer <i class="fas fa-arrow-right"></i>
            </button>
          </section>

          <!-- ══════════════════════════════════════════════════════════════════ -->
          <!--  ÉTAPE 2 — PAIEMENT                                               -->
          <!-- ══════════════════════════════════════════════════════════════════ -->
          <section v-if="step === 'payment'">

            <div class="flex items-center justify-between bg-white border border-neutral-200 rounded-xl px-5 py-3.5 mb-6 shadow-sm">
              <button class="flex items-center gap-2 text-sm text-neutral-400 hover:text-neutral-700 transition-colors" @click="step = 'plans'">
                <i class="fas fa-chevron-left"></i> Modifier
              </button>
              <div class="text-right">
                <span class="text-sm font-semibold text-neutral-900 block">Formule {{ currentPlan.name }}</span>
                <span class="text-xs font-mono text-neutral-400">{{ currentPlan.price_int }}{{ currentPlan.price_dec }} € / mois HT</span>
              </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">
              <div class="space-y-4">

                <!-- CB enregistrées -->
                <div v-if="paymentMethods.length" class="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
                  <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-4 flex items-center gap-2">
                    <span class="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded">02</span>
                    Moyen de paiement
                  </p>
                  <div class="space-y-2.5">
                    <label v-for="pm in paymentMethods" :key="pm.id" class="flex items-center justify-between border-2 rounded-lg px-4 py-3 cursor-pointer transition-all" :class="selectedPmId === pm.id ? 'border-orange-500 bg-orange-50' : 'border-neutral-200 hover:border-neutral-300'">
                      <input type="radio" :value="pm.id" v-model="selectedPmId" class="absolute opacity-0 w-0 h-0" />
                      <div class="flex items-center gap-3">
                        <i :class="brandIcon(pm.brand)" class="text-2xl text-neutral-400"></i>
                        <div>
                          <p class="text-sm font-mono font-medium text-neutral-800">•••• •••• •••• {{ pm.last4 }}</p>
                          <p class="text-xs text-neutral-400">Expire {{ pm.exp_month }}/{{ pm.exp_year }}</p>
                        </div>
                      </div>
                      <div class="flex items-center gap-2">
                        <span v-if="pm.is_default" class="text-xs font-mono bg-neutral-100 text-neutral-400 px-2 py-0.5 rounded uppercase tracking-wide">Par défaut</span>
                        <div class="w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all" :class="selectedPmId === pm.id ? 'bg-orange-500 border-orange-500' : 'border-neutral-300'">
                          <div v-if="selectedPmId === pm.id" class="w-1.5 h-1.5 rounded-full bg-white"></div>
                        </div>
                      </div>
                    </label>
                  </div>
                  <button class="mt-4 w-full border-2 border-dashed border-neutral-200 hover:border-orange-400 hover:bg-orange-50 text-orange-500 text-sm py-2.5 rounded-lg flex items-center justify-center gap-2 transition-all" @click="showNewCard = !showNewCard">
                    <i :class="showNewCard ? 'fas fa-minus' : 'fas fa-plus'"></i>
                    {{ showNewCard ? 'Annuler' : 'Utiliser une autre carte' }}
                  </button>
                </div>

                <!-- Formulaire carte Stripe (v-show pour garder les refs dans le DOM) -->
                <div v-show="!paymentMethods.length || showNewCard" class="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
                  <p class="text-xs font-mono uppercase tracking-widest text-neutral-400 mb-5 flex items-center gap-2">
                    <span class="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded">{{ paymentMethods.length ? '—' : '02' }}</span>
                    {{ paymentMethods.length ? 'Nouvelle carte' : 'Carte bancaire' }}
                  </p>
                  <div class="space-y-4">
                    <div>
                      <label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Numéro de carte</label>
                      <div ref="cardNumber" class="border border-neutral-200 rounded-lg px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div>
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Expiration</label>
                        <div ref="cardExpiry" class="border border-neutral-200 rounded-lg px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div>
                      </div>
                      <div>
                        <label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">CVC</label>
                        <div ref="cardCvc" class="border border-neutral-200 rounded-lg px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div>
                      </div>
                    </div>
                    <div>
                      <label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1.5">Nom du titulaire</label>
                      <input v-model="cardHolder" type="text" class="w-full border border-neutral-200 rounded-lg px-4 py-3 bg-neutral-50 text-sm text-neutral-900 placeholder-neutral-300 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 transition-all" placeholder="Jean Dupont" autocomplete="cc-name" />
                    </div>
                  </div>
                  <div class="mt-4 flex flex-wrap items-center gap-3 px-4 py-3 bg-neutral-50 border border-neutral-100 rounded-lg text-xs text-neutral-400 font-mono">
                    <span class="flex items-center gap-1.5"><i class="fas fa-lock text-green-500"></i> SSL 256 bits</span>
                    <span class="text-neutral-200">·</span>
                    <span class="flex items-center gap-1.5"><i class="fab fa-stripe" style="color:#635bff;"></i> Sécurisé par Stripe</span>
                    <span class="text-neutral-200">·</span>
                    <span class="flex items-center gap-1.5"><i class="fas fa-id-card text-blue-400"></i> 3D Secure</span>
                  </div>
                </div>

                <div v-if="errorMsg" class="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3.5 text-sm text-red-700">
                  <i class="fas fa-exclamation-triangle mt-0.5 shrink-0"></i>
                  <span>{{ errorMsg }}</span>
                </div>

                <button class="w-full flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white font-semibold py-4 rounded-xl shadow-[0_4px_14px_rgba(0,0,0,0.2)] transition-all disabled:opacity-50 disabled:cursor-not-allowed" :disabled="paying || !canPay" @click="pay">
                  <span v-if="paying"><i class="fas fa-circle-notch fa-spin"></i>&nbsp; Traitement en cours…</span>
                  <span v-else><i class="fas fa-lock"></i>&nbsp; Payer {{ currentPlan.price_int }}{{ currentPlan.price_dec }} € / mois</span>
                </button>

                <p class="text-center text-xs text-neutral-400 leading-relaxed">
                  En confirmant, vous acceptez nos <a href="https://storage.victoranw.fr/file/x3jvJaPzGU" target="_blank" class="text-orange-500 hover:underline">Conditions Générales d'Utilisation</a> et nos <a href="https://storage.victoranw.fr/file/ADo4JPI8RA" target="_blank" class="text-orange-500 hover:underline">Conditions Générales de Vente</a>.
                  Abonnement renouvelé automatiquement · Résiliable à tout moment.
                </p>
              </div>

              <!-- Récapitulatif -->
              <aside class="bg-neutral-900 text-white rounded-xl p-6 shadow-xl lg:sticky lg:top-6">
                <h3 class="font-bold text-base mb-5">Récapitulatif</h3>
                <div class="mb-1">
                  <div class="flex justify-between text-sm text-white/50 py-2 border-b border-white/5">
                    <span>Formule {{ currentPlan.name }}</span><span>{{ currentPlan.price_ht }} €</span>
                  </div>
                  <div class="flex justify-between text-sm text-white/50 py-2 border-b border-white/5">
                    <span>TVA (20 %)</span><span>{{ currentPlan.price_tva }} €</span>
                  </div>
                  <div class="flex justify-between font-bold py-3">
                    <span>Total mensuel</span><span>{{ currentPlan.price_int }}{{ currentPlan.price_dec }} €</span>
                  </div>
                </div>
                <div class="border-t border-white/10 my-4"></div>
                <p class="text-xs font-mono uppercase tracking-widest text-white/30 mb-3">Inclus</p>
                <ul class="space-y-2 mb-5">
                  <li v-for="f in currentPlan.features.filter(x => x.included)" :key="f.label" class="flex items-center gap-2 text-xs text-white/60">
                    <i class="fas fa-check text-orange-400" style="font-size:10px;"></i> {{ f.label }}
                  </li>
                </ul>
                <div class="border-t border-white/10 my-4"></div>
                <div class="space-y-2">
                  <div class="flex items-center gap-2 text-xs text-white/30"><i class="fas fa-sync-alt text-orange-400"></i><span>Renouvellement automatique</span></div>
                  <div class="flex items-center gap-2 text-xs text-white/30"><i class="fas fa-times-circle text-orange-400"></i><span>Résiliation sans frais</span></div>
                  <div class="flex items-center gap-2 text-xs text-white/30"><i class="fas fa-file-alt text-orange-400"></i><span>Facture PDF par email</span></div>
                </div>
                <div class="border-t border-white/10 mt-4 pt-4 flex items-center gap-3">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/5/5c/Visa_Inc._logo_%282021%E2%80%93present%29.svg/3840px-Visa_Inc._logo_%282021%E2%80%93present%29.svg.png" alt="Visa" class="h-5 opacity-50" />
                  <img src="https://upload.wikimedia.org/wikipedia/commons/a/a4/Mastercard_2019_logo.svg" alt="Mastercard" class="h-4 opacity-40 invert" />
                  <i class="fab fa-stripe text-2xl opacity-50" style="color:#a5a3ff;"></i>
                </div>
              </aside>
            </div>
          </section>

          <!-- ══════════════════════════════════════════════════════════════════ -->
          <!--  SUCCÈS                                                           -->
          <!-- ══════════════════════════════════════════════════════════════════ -->
          <section v-if="step === 'success'" class="flex items-center justify-center min-h-96">
            <div class="bg-white border border-neutral-200 rounded-2xl p-12 max-w-md w-full text-center shadow-xl">
              <div class="w-16 h-16 rounded-full bg-orange-500 flex items-center justify-center text-white text-2xl mx-auto mb-6 shadow-[0_8px_24px_rgba(240,90,0,0.35)]">
                <i class="fas fa-check"></i>
              </div>
              <h2 class="text-2xl font-bold text-neutral-900 mb-3">Abonnement activé !</h2>
              <p class="text-sm text-neutral-400 leading-relaxed mb-8">
                Votre formule <strong class="text-neutral-700">{{ currentPlan.name }}</strong> est maintenant active.<br>
                Une facture a été envoyée à <strong class="text-neutral-700">{{ societe.mail }}</strong>.
              </p>
              <button class="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-lg transition-all shadow-[0_4px_14px_rgba(240,90,0,0.3)]" @click="goToSubscribed">
                Accéder à mon espace <i class="fas fa-arrow-right"></i>
              </button>
            </div>
          </section>

          <!-- ══════════════════════════════════════════════════════════════════ -->
          <!--  ESPACE ABONNÉ                                                    -->
          <!-- ══════════════════════════════════════════════════════════════════ -->
          <section v-if="step === 'subscribed'">

            <!-- Statut abonnement -->
            <div class="bg-neutral-900 text-white rounded-xl px-6 py-5 mb-6 flex items-center justify-between flex-wrap gap-4 shadow-xl">
              <div class="flex items-center gap-4">
                <div class="relative w-2.5 h-2.5 shrink-0">
                  <div class="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-60"></div>
                  <div class="w-2.5 h-2.5 rounded-full bg-green-400 relative"></div>
                </div>
                <div>
                  <p class="text-xs font-mono uppercase tracking-widest text-white/30 mb-0.5">Abonnement actif</p>
                  <p class="font-semibold">Formule <strong>{{ activePlanName }}</strong></p>
                </div>
              </div>
              <div class="flex flex-col items-end gap-2">
                <p class="text-xs text-white/30 font-mono flex items-center gap-1.5">
                  <i class="fas fa-redo-alt"></i> Renouvellement le {{ renewalDate }}
                </p>
                <button class="text-xs border border-white/20 hover:border-red-400 hover:text-red-400 text-white/50 px-3 py-1.5 rounded-lg transition-all" @click="confirmResil = true">
                  Résilier l'abonnement
                </button>
              </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">

              <!-- ── Colonne paiement ────────────────────────────────────────── -->
              <div class="space-y-4">

                <!-- CB par défaut -->
                <div class="bg-white border border-neutral-200 rounded-xl p-5 shadow-sm">
                  <div class="flex items-center justify-between mb-4">
                    <h2 class="font-bold text-neutral-900">Carte de paiement</h2>
                    <button class="text-xs border border-neutral-200 hover:border-orange-400 hover:text-orange-500 text-neutral-400 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-all" @click="openChangeCardPanel">
                      <i class="fas fa-exchange-alt"></i> Changer
                    </button>
                  </div>

                  <div v-if="loadingCards" class="flex justify-center py-4">
                    <div class="w-5 h-5 rounded-full border-2 border-neutral-200 border-t-orange-500 animate-spin"></div>
                  </div>

                  <!-- Carte par défaut affichée -->
                  <div v-else-if="defaultCard" class="flex items-center gap-3 border border-neutral-100 rounded-lg px-4 py-3.5 bg-neutral-50">
                    <i :class="brandIcon(defaultCard.brand)" class="text-3xl text-neutral-400"></i>
                    <div class="flex-1">
                      <p class="text-sm font-mono font-semibold text-neutral-800">{{ defaultCard.brand }} •••• {{ defaultCard.last4 }}</p>
                      <p class="text-xs text-neutral-400">Expire {{ defaultCard.exp_month }}/{{ defaultCard.exp_year }}</p>
                    </div>
                    <span class="text-xs bg-orange-50 text-orange-500 px-2 py-0.5 rounded-full font-mono">défaut</span>
                  </div>
                  <p v-else class="text-sm text-neutral-400">Aucune carte enregistrée.</p>
                </div>

                <!-- Panel changement de carte (v-show pour garder les refs) -->
                <div v-show="changeCardPanel" class="bg-white border border-orange-200 rounded-xl p-5 shadow-sm">
                  <div class="flex items-center justify-between mb-4">
                    <h3 class="font-semibold text-neutral-900 text-sm">Nouvelle carte par défaut</h3>
                    <button class="text-neutral-400 hover:text-neutral-700 text-sm" @click="closeChangeCardPanel">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>

                  <!-- Sélectionner une carte existante -->
                  <div v-if="otherCards.length" class="space-y-2 mb-4">
                    <p class="text-xs font-mono uppercase tracking-wider text-neutral-400 mb-2">Cartes enregistrées</p>
                    <label v-for="pm in otherCards" :key="pm.id" class="flex items-center gap-3 border-2 rounded-lg px-3 py-2.5 cursor-pointer transition-all" :class="newDefaultPmId === pm.id ? 'border-orange-500 bg-orange-50' : 'border-neutral-200 hover:border-neutral-300'">
                      <input type="radio" :value="pm.id" v-model="newDefaultPmId" class="absolute opacity-0 w-0 h-0" />
                      <i :class="brandIcon(pm.brand)" class="text-xl text-neutral-400"></i>
                      <div class="flex-1">
                        <p class="text-sm font-mono text-neutral-800">•••• {{ pm.last4 }}</p>
                        <p class="text-xs text-neutral-400">{{ pm.exp_month }}/{{ pm.exp_year }}</p>
                      </div>
                      <div class="w-4 h-4 rounded-full border-2 transition-all" :class="newDefaultPmId === pm.id ? 'bg-orange-500 border-orange-500' : 'border-neutral-300'"></div>
                    </label>

                    <button v-if="newDefaultPmId" class="w-full mt-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50" :disabled="settingDefault" @click="setDefaultCard">
                      <span v-if="settingDefault"><i class="fas fa-circle-notch fa-spin"></i> Mise à jour…</span>
                      <span v-else><i class="fas fa-check"></i> Utiliser cette carte</span>
                    </button>

                    <div class="relative flex items-center gap-3 my-3">
                      <div class="flex-1 border-t border-neutral-200"></div>
                      <span class="text-xs text-neutral-400 font-mono">ou</span>
                      <div class="flex-1 border-t border-neutral-200"></div>
                    </div>
                  </div>

                  <!-- Nouvelle carte -->
                  <p class="text-xs font-mono uppercase tracking-wider text-neutral-400 mb-3">Ajouter une nouvelle carte</p>
                  <div class="space-y-3">
                    <div>
                      <label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1">Numéro de carte</label>
                      <div ref="changeCardNumber" class="border border-neutral-200 rounded-lg px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div>
                    </div>
                    <div class="grid grid-cols-2 gap-3">
                      <div>
                        <label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1">Expiration</label>
                        <div ref="changeCardExpiry" class="border border-neutral-200 rounded-lg px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div>
                      </div>
                      <div>
                        <label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1">CVC</label>
                        <div ref="changeCardCvc" class="border border-neutral-200 rounded-lg px-4 bg-neutral-50" style="min-height:46px;padding-top:12px;padding-bottom:12px;"></div>
                      </div>
                    </div>
                    <div>
                      <label class="block text-xs font-mono uppercase tracking-wider text-neutral-400 mb-1">Titulaire</label>
                      <input v-model="changeCardHolder" type="text" class="w-full border border-neutral-200 rounded-lg px-4 py-2.5 bg-neutral-50 text-sm placeholder-neutral-300 focus:outline-none focus:border-orange-500 transition-all" placeholder="Jean Dupont" />
                    </div>
                  </div>

                  <div class="mt-3 flex flex-wrap items-center gap-2 px-3 py-2 bg-neutral-50 border border-neutral-100 rounded-lg text-xs text-neutral-400 font-mono">
                    <span class="flex items-center gap-1"><i class="fas fa-lock text-green-500"></i> SSL</span>
                    <span class="text-neutral-200">·</span>
                    <span class="flex items-center gap-1"><i class="fab fa-stripe" style="color:#635bff;"></i> Stripe</span>
                    <span class="text-neutral-200">·</span>
                    <span class="flex items-center gap-1"><i class="fas fa-id-card text-blue-400"></i> 3DS</span>
                  </div>

                  <div v-if="changeCardError" class="mt-3 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-700">
                    <i class="fas fa-exclamation-triangle mt-0.5 shrink-0"></i> {{ changeCardError }}
                  </div>

                  <button class="mt-4 w-full flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white text-sm font-semibold py-3 rounded-xl transition-all disabled:opacity-50" :disabled="addingCard" @click="submitNewDefaultCard">
                    <span v-if="addingCard"><i class="fas fa-circle-notch fa-spin"></i> Enregistrement…</span>
                    <span v-else><i class="fas fa-lock"></i> Enregistrer et utiliser cette carte</span>
                  </button>
                </div>

                <!-- Autres cartes enregistrées -->
                <div v-if="paymentMethods.length > 1" class="bg-white border border-neutral-200 rounded-xl p-5 shadow-sm">
                  <h2 class="font-bold text-neutral-900 mb-3 text-sm">Autres cartes</h2>
                  <div class="space-y-2">
                    <div v-for="pm in otherCards" :key="pm.id" class="flex items-center justify-between border border-neutral-100 rounded-lg px-3.5 py-2.5">
                      <div class="flex items-center gap-3">
                        <i :class="brandIcon(pm.brand)" class="text-xl text-neutral-300"></i>
                        <div>
                          <p class="text-sm font-mono font-medium text-neutral-700">•••• {{ pm.last4 }}</p>
                          <p class="text-xs text-neutral-400">{{ pm.exp_month }}/{{ pm.exp_year }}</p>
                        </div>
                      </div>
                      <button class="text-neutral-300 hover:text-red-400 text-sm transition-colors" @click="deleteCard(pm.id)" title="Supprimer">
                        <i class="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- ── Factures ───────────────────────────────────────────────── -->
              <div class="bg-white border border-neutral-200 rounded-xl p-5 shadow-sm">
                <h2 class="font-bold text-neutral-900 mb-4">Historique des factures</h2>
                <div v-if="loadingInvoices" class="flex justify-center py-6">
                  <div class="w-5 h-5 rounded-full border-2 border-neutral-200 border-t-orange-500 animate-spin"></div>
                </div>
                <div v-else-if="factures.length" class="overflow-x-auto">
                  <table class="w-full text-sm">
                    <thead>
                      <tr class="border-b-2 border-neutral-100">
                        <th class="text-left text-xs font-mono uppercase tracking-wider text-neutral-400 font-medium pb-3 pr-4">N° Facture</th>
                        <th class="text-left text-xs font-mono uppercase tracking-wider text-neutral-400 font-medium pb-3 pr-4">Date</th>
                        <th class="text-left text-xs font-mono uppercase tracking-wider text-neutral-400 font-medium pb-3 pr-4 hidden md:table-cell">Description</th>
                        <th class="text-right text-xs font-mono uppercase tracking-wider text-neutral-400 font-medium pb-3 pr-4">TTC</th>
                        <th class="text-left text-xs font-mono uppercase tracking-wider text-neutral-400 font-medium pb-3 pr-4">Statut</th>
                        <th class="pb-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="f in factures" :key="f.id" class="border-b border-neutral-50 hover:bg-neutral-50 transition-colors">
                        <td class="py-3.5 pr-4 font-mono text-xs font-medium text-neutral-700">{{ f.numero || ('FAC-' + f.id) }}</td>
                        <td class="py-3.5 pr-4 text-xs text-neutral-400 whitespace-nowrap">{{ (f.date_paiement || f.created_at).slice(0, 10) }}</td>
                        <td class="py-3.5 pr-4 text-xs text-neutral-400 max-w-xs truncate hidden md:table-cell">{{ f.description }}</td>
                        <td class="py-3.5 pr-4 text-right font-mono text-sm font-semibold text-neutral-800 whitespace-nowrap">{{ fmt(f.montant_ttc, f.devise) }}</td>
                        <td class="py-3.5 pr-4">
                          <span class="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wide px-2.5 py-1 rounded" :class="badgeClass(f.statut)">
                            <i :class="badgeIcon(f.statut)" style="font-size:10px;"></i>
                            {{ badgeLabel(f.statut) }}
                          </span>
                        </td>
                        <td class="py-3.5">
                          <a v-if="f.vosfactures_url" :href="f.vosfactures_url" target="_blank" class="w-7 h-7 rounded-md bg-red-50 hover:bg-red-100 text-red-400 flex items-center justify-center transition-colors" title="PDF">
                            <i class="fas fa-file-pdf text-xs"></i>
                          </a>
                          <span v-else class="text-neutral-300">—</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-else class="text-center py-12 text-neutral-400">
                  <i class="fas fa-file-invoice text-4xl opacity-20 mb-3 block"></i>
                  <p class="text-sm leading-relaxed">Aucune facture pour le moment.<br>Elle apparaîtra ici après votre premier paiement.</p>
                </div>
              </div>
            </div>
          </section>

        </div>

        <!-- ══════════════════════════════════════════════════════════════════════ -->
        <!--  MODAL — Résiliation                                                   -->
        <!-- ══════════════════════════════════════════════════════════════════════ -->
        <div v-if="confirmResil" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" @click.self="confirmResil = false">
          <div class="bg-white rounded-2xl p-7 w-full max-w-sm shadow-2xl">
            <div class="flex items-center justify-between mb-5">
              <h3 class="font-bold text-lg text-neutral-900">Résilier l'abonnement</h3>
              <button class="text-neutral-400 hover:text-neutral-700" @click="confirmResil = false"><i class="fas fa-times"></i></button>
            </div>
            <p class="text-sm text-neutral-500 leading-relaxed mb-6">
              Afin de résilier votre abonnement, merci de nous contacter par email à contact@termoflow.fr en nous indiquant votre numéro de SIRET: <strong>{{ $account.account.societe.siret }}</strong>.
            </p>
          </div>
        </div>

      </div>
    </app-template>
  </div>
</template>

<script>
/* global Stripe */
import env from '@/services/env.service'

const STRIPE_STYLE = {
  base: {
    fontSize: '15px',
    color: '#1a1a1a',
    fontSmoothing: 'antialiased',
    '::placeholder': { color: '#bbbbbb' },
  },
  invalid: { color: '#c0392b', iconColor: '#c0392b' },
}

export default {
  name: 'MonAbonnement',

  data () {
    return {
      loading: true,
      step: 'plans', // plans | payment | success | subscribed

      // ── Formules ───────────────────────────────────────────────────────
      plans: [
        {
          id: 'independant',
          name: 'Indépendant',
          tagline: "L'arsenal complet pour l'artisan qui veut une structure de grand groupe.",
          icon: 'fas fa-user',
          recommended: false,
          price_int: '30',
          price_dec: '',
          price_ht: '30',
          stripe_amount: 3000,
          stripe_product_id: 'prod_UJyeb8na3jba1h',
          features: [
            { label: 'Gestion opérationnelle (interventions, planning, trajets)', included: true },
            { label: 'App mobile métier (offline, rapports, signatures)', included: true },
            { label: 'CERFA & conformité (auto + archivage légal)', included: true },
            { label: "Base de connaissance (72 000 appareils)", included: true },
            { label: 'Stock & fluides (traçabilité gaz, suivi bouteilles)', included: true },
            { label: 'Relation client (contrats, SMS, historique)', included: true },
            { label: 'Pilotage & intégration (stats, exports, facturation)', included: true },
          ],
          cta: 'Démarrer mon essai',
        },
        {
          id: 'entreprise',
          name: 'Entreprise',
          tagline: "Pour piloter vos équipes, vos stocks et votre image de marque avec précision.",
          icon: 'fas fa-building',
          recommended: true,
          price_int: '55',
          price_dec: '',
          price_ht: '55',
          stripe_amount: 5500,
          stripe_product_id: 'prod_UJyeb8na3jba1h',
          features: [
            { label: "Tout l'indépendant", included: true },
            { label: "Pilotage d'équipe (multi-techniciens, charge, performance)", included: true },
            { label: 'Logistique avancée (multi-stocks, mouvements)', included: true },
            { label: 'Portail client premium (accès sécurisé 24/7)', included: true },
            { label: 'Marketing automatisé (avis, fidélisation)', included: true },
            { label: 'Reporting stratégique (analyses avancées)', included: true },
            { label: 'Audit & sécurité (traçabilité renforcée)', included: true },
          ],
          cta: 'Demander une démo',
          badge: 'Recommandé pour la croissance',
        },
      ],

      selectedPlan: 'pro',

      // ── Stripe ────────────────────────────────────────────────────────
      stripe: null,
      elements: null,

      // Éléments tunnel paiement
      elNumber: null,
      elExpiry: null,
      elCvc: null,
      cardHolder: '',
      stripeReady: false,

      // Éléments changement carte (espace abonné)
      changeElNumber: null,
      changeElExpiry: null,
      changeElCvc: null,
      changeCardHolder: '',
      changeCardReady: false,

      // ── Données ────────────────────────────────────────────────────────
      paymentMethods: [],
      selectedPmId: null,   // pour le tunnel de paiement
      showNewCard: false,
      loadingCards: false,
      factures: [],
      loadingInvoices: false,

      // ── Espace abonné ─────────────────────────────────────────────────
      changeCardPanel: false,
      newDefaultPmId: null,   // carte existante sélectionnée pour devenir défaut
      settingDefault: false,
      addingCard: false,
      changeCardError: null,

      // ── UI ─────────────────────────────────────────────────────────────
      paying: false,
      errorMsg: null,
      confirmResil: false,
    }
  },

  computed: {
    societe () {
      return this.$account?.account?.societe || {}
    },
    currentPlan () {
      return this.plans.find(p => p.id === this.selectedPlan) || this.plans[0]
    },
    // Nom de la formule active basé sur stripe_product_id de la société
    activePlanName () {
      const match = this.plans.find(p => p.stripe_product_id === this.societe.stripe_product_id)
      return match?.name || 'Pro'
    },
    renewalDate () {
      const d = new Date()
      d.setMonth(d.getMonth() + 1)
      return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
    },
    canPay () {
      if (this.paymentMethods.length && !this.showNewCard && this.selectedPmId) return true
      return this.stripeReady
    },
    // Carte par défaut
    defaultCard () {
      return this.paymentMethods.find(pm => pm.is_default) || this.paymentMethods[0] || null
    },
    // Toutes les cartes sauf la carte par défaut
    otherCards () {
      return this.paymentMethods.filter(pm => !pm.is_default)
    },
  },

  async mounted () {
    await this.init()
  },

  beforeDestroy () {
    this.destroyAll()
  },

  methods: {

    // ── Init ───────────────────────────────────────────────────────────────
    async init () {
      this.loading = true
      try {
        if (!this.societe?.id) return

        // Détection de l'abonnement via stripe_product_id (source de vérité)
        if (this.societe.stripe_product_id) {
          await Promise.all([this.loadCards(), this.loadInvoices()])
          this.step = 'subscribed'
          return
        }

        // Pas abonné mais a un compte Stripe → pré-charger ses cartes
        if (this.societe.stripe_id) {
          await this.loadCards()
        }

        this.step = 'plans'
      } catch (e) {
        console.error('MonAbonnement init', e)
      } finally {
        this.loading = false
      }
    },

    // ── Navigation ─────────────────────────────────────────────────────────
    async goToPayment () {
      this.step = 'payment'
      this.errorMsg = null
      this.stripeReady = false
      this.loadCards()
      await this.$nextTick()
      await this.$nextTick()
      await this.loadStripe()
      this.mountPaymentElements()
    },

    async goToSubscribed () {
      this.step = 'subscribed'
      await this.loadCards()
      await this.loadInvoices()
    },

    // ── Stripe ─────────────────────────────────────────────────────────────
    async loadStripe () {
      if (this.stripe) return

      const pk = env.stripeKey
      if (!pk) {
        this.errorMsg = 'Configuration Stripe manquante. Contactez le support.'
        console.error('[MonAbonnement] env.stripeKey est vide dans env.service.js')
        throw new Error('Stripe PK manquante')
      }

      if (!window.Stripe) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script')
          s.src = 'https://js.stripe.com/v3/'
          s.onload = resolve
          s.onerror = () => reject(new Error('Impossible de charger Stripe.js'))
          document.head.appendChild(s)
        })
      }

      this.stripe   = Stripe(pk)
      this.elements = this.stripe.elements()
    },

    mountElements (refNum, refExp, refCvc, onReady, onChange) {
      const numEl = this.$refs[refNum]
      const expEl = this.$refs[refExp]
      const cvcEl = this.$refs[refCvc]

      if (!numEl || !expEl || !cvcEl) {
        setTimeout(() => this.mountElements(refNum, refExp, refCvc, onReady, onChange), 100)
        return
      }

      const elNum = this.elements.create('cardNumber', { style: STRIPE_STYLE, showIcon: true })
      const elExp = this.elements.create('cardExpiry', { style: STRIPE_STYLE })
      const elCvc = this.elements.create('cardCvc',    { style: STRIPE_STYLE })

      numEl.innerHTML = ''
      expEl.innerHTML = ''
      cvcEl.innerHTML = ''

      elNum.mount(numEl)
      elExp.mount(expEl)
      elCvc.mount(cvcEl)

      elNum.on('ready', onReady)
      elNum.on('change', e => { if (onChange) onChange(e.error?.message || null) })

      return { elNum, elExp, elCvc }
    },

    mountPaymentElements () {
      ;[this.elNumber, this.elExpiry, this.elCvc].forEach(el => {
        if (el) { try { el.destroy() } catch (_) {} }
      })

      const els = this.mountElements(
        'cardNumber', 'cardExpiry', 'cardCvc',
        () => { this.stripeReady = true },
        msg => { this.errorMsg = msg }
      )

      if (els) {
        this.elNumber = els.elNum
        this.elExpiry = els.elExp
        this.elCvc    = els.elCvc
      }
    },

    async mountChangeCardElements () {
      ;[this.changeElNumber, this.changeElExpiry, this.changeElCvc].forEach(el => {
        if (el) { try { el.destroy() } catch (_) {} }
      })

      const els = this.mountElements(
        'changeCardNumber', 'changeCardExpiry', 'changeCardCvc',
        () => { this.changeCardReady = true },
        msg => { this.changeCardError = msg }
      )

      if (els) {
        this.changeElNumber = els.elNum
        this.changeElExpiry = els.elExp
        this.changeElCvc    = els.elCvc
      }
    },

    destroyAll () {
      const all = [this.elNumber, this.elExpiry, this.elCvc, this.changeElNumber, this.changeElExpiry, this.changeElCvc]
      all.forEach(el => { if (el) { try { el.destroy() } catch (_) {} } })
    },

    // ── API ────────────────────────────────────────────────────────────────
    async loadCards () {
      if (!this.societe?.id) return
      this.loadingCards = true
      try {
        const res = await this.$request.get(`/stripe/payment-methods/${this.societe.id}`)
        this.paymentMethods = res.payment_methods || []
        const def = this.paymentMethods.find(p => p.is_default)
        this.selectedPmId = def?.id || this.paymentMethods[0]?.id || null
      } catch (e) {
        console.error('loadCards', e)
      } finally {
        this.loadingCards = false
      }
    },

    async loadInvoices () {
      if (!this.societe?.id) return
      this.loadingInvoices = true
      try {
        const res = await this.$request.get(`/stripe/factures/${this.societe.id}`)
        this.factures = res.factures || []
      } catch (e) {
        console.error('loadInvoices', e)
      } finally {
        this.loadingInvoices = false
      }
    },

    async ensureCustomer () {
      if (this.societe.stripe_id) return true
      try {
        await this.$request.post('/stripe/customers', { societe_id: this.societe.id })
        return true
      } catch (e) {
        this.errorMsg = 'Impossible de créer votre profil de paiement. Veuillez réessayer.'
        return false
      }
    },

    // ── Paiement ───────────────────────────────────────────────────────────
    async pay () {
      this.errorMsg = null
      this.paying = true
      try {
        if (!await this.ensureCustomer()) return

        let pmId = this.selectedPmId
        if (!pmId || this.showNewCard) {
          pmId = await this.saveCard(this.elNumber, this.cardHolder)
          if (!pmId) return
        }

        await this.$request.post('/stripe/charge', {
          societe_id:        this.societe.id,
          amount:            this.currentPlan.stripe_amount,
          currency:          'eur',
          description:       `Abonnement ${this.currentPlan.name}`,
          payment_method_id: pmId,
          taux_tva:          20,
          stripe_product_id: this.currentPlan.stripe_product_id,
        })

        // Mettre à jour le compte local pour refléter l'abonnement immédiatement
        if (this.$account?.account?.societe) {
          this.$account.account.societe.stripe_product_id = this.currentPlan.stripe_product_id
        }

        this.step = 'success'
      } catch (e) {
        this.errorMsg = e?.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.'
      } finally {
        this.paying = false
      }
    },

    // ── Enregistrer une carte via SetupIntent ──────────────────────────────
    async saveCard (cardElement, holderName, isChange = false) {
      try {
        const siRes = await this.$request.post('/stripe/setup-intent', { societe_id: this.societe.id })
        const { setupIntent, error } = await this.stripe.confirmCardSetup(siRes.client_secret, {
          payment_method: {
            card: cardElement,
            billing_details: { name: holderName || this.societe.raison_sociale || '' },
          },
        })
        if (error) {
          if (isChange) this.changeCardError = error.message
          else this.errorMsg = error.message
          return null
        }
        await this.$request.post('/stripe/payment-methods/attach', {
          societe_id:        this.societe.id,
          payment_method_id: setupIntent.payment_method,
          set_as_default:    true,
        })
        return setupIntent.payment_method
      } catch (e) {
        const msg = e?.response?.data?.message || "Erreur lors de l'enregistrement de la carte."
        if (isChange) this.changeCardError = msg
        else this.errorMsg = msg
        return null
      }
    },

    // ── Gestion carte par défaut (espace abonné) ───────────────────────────
    async openChangeCardPanel () {
      this.changeCardPanel  = true
      this.changeCardError  = null
      this.newDefaultPmId   = null
      this.changeCardHolder = ''
      this.changeCardReady  = false
      await this.$nextTick()
      await this.$nextTick()
      await this.loadStripe()
      this.mountChangeCardElements()
    },

    closeChangeCardPanel () {
      this.changeCardPanel = false
      ;[this.changeElNumber, this.changeElExpiry, this.changeElCvc].forEach(el => {
        if (el) { try { el.destroy() } catch (_) {} }
      })
      this.changeElNumber = this.changeElExpiry = this.changeElCvc = null
    },

    // Définir une carte existante comme défaut
    async setDefaultCard () {
      if (!this.newDefaultPmId) return
      this.settingDefault = true
      try {
        await this.$request.post('/stripe/payment-methods/set-default', {
          societe_id:        this.societe.id,
          payment_method_id: this.newDefaultPmId,
        })
        await this.loadCards()
        this.closeChangeCardPanel()
      } catch (e) {
        this.changeCardError = e?.response?.data?.message || 'Erreur lors de la mise à jour.'
      } finally {
        this.settingDefault = false
      }
    },

    // Enregistrer une nouvelle carte et la définir comme défaut
    async submitNewDefaultCard () {
      this.changeCardError = null
      this.addingCard = true
      try {
        if (!await this.ensureCustomer()) return
        const pmId = await this.saveCard(this.changeElNumber, this.changeCardHolder, true)
        if (pmId) {
          await this.loadCards()
          this.closeChangeCardPanel()
        }
      } finally {
        this.addingCard = false
      }
    },

    async deleteCard (pmId) {
      if (!window.confirm('Supprimer cette carte ?')) return
      try {
        await this.$request.delete(`/stripe/payment-methods/${pmId}`)
        await this.loadCards()
      } catch {
        alert('Impossible de supprimer cette carte.')
      }
    },

    cancelSub () {
      this.confirmResil = false
      alert("Résiliation prise en compte. Votre accès reste actif jusqu'à la fin de la période.")
    },

    // ── Helpers ────────────────────────────────────────────────────────────
    brandIcon (brand) {
      const b = (brand || '').toLowerCase()
      if (b === 'visa')       return 'fab fa-cc-visa'
      if (b === 'mastercard') return 'fab fa-cc-mastercard'
      if (b === 'amex')       return 'fab fa-cc-amex'
      if (b === 'discover')   return 'fab fa-cc-discover'
      return 'fas fa-credit-card'
    },
    fmt (n, devise) {
      return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: devise || 'EUR' }).format(n)
    },
    badgeLabel (s) {
      return { payee: 'Payée', en_attente: 'En attente', echouee: 'Échouée', remboursee: 'Remboursée' }[s] || s
    },
    badgeClass (s) {
      return {
        payee:      'bg-green-50 text-green-700',
        en_attente: 'bg-yellow-50 text-yellow-700',
        echouee:    'bg-red-50 text-red-700',
        remboursee: 'bg-purple-50 text-purple-700',
      }[s] || 'bg-neutral-100 text-neutral-500'
    },
    badgeIcon (s) {
      return {
        payee:      'fas fa-check-circle',
        en_attente: 'fas fa-clock',
        echouee:    'fas fa-times-circle',
        remboursee: 'fas fa-undo-alt',
      }[s] || 'fas fa-circle'
    },
  },
}
</script>

<!-- Pas de "scoped" — intentionnel pour ne pas bloquer les iframes Stripe -->
<style scoped>
[data-elements-stable-field-name="cardNumber"] iframe,
[data-elements-stable-field-name="cardExpiry"] iframe,
[data-elements-stable-field-name="cardCvc"] iframe {
  display: block !important;
  width: 100% !important;
}
</style>